import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { APP } from '../config/app';
import { triggerRideSos } from '../services/firebase';
import {
  buildTelUrl, buildSosTelUrl, openWhatsappChat,
} from '../utils/support';

/**
 * Anna-side emergency sheet. Mirrors the passenger SOS:
 *  - Call 112 (also raises the server alert → MyTownly ops + admin inbox)
 *  - Call MyTownly support (also raises the alert)
 *  - Alert my emergency contact on WhatsApp with the live trip link
 * Every action notifies MyTownly — pressing 112 alone is enough.
 */
export default function DriverSosSheet({ t, rideId, onClose }) {
  const [busy, setBusy] = useState(false);
  const [alerted, setAlerted] = useState(false);
  const sosDataRef = useRef(null);
  const supportTel = buildTelUrl();
  const hasSupport = supportTel !== '#';

  const tr = (key, fallback, vars) => {
    const v = t(key, vars);
    return v === key ? fallback : v;
  };

  const ensureSosData = async () => {
    if (sosDataRef.current) return sosDataRef.current;
    const data = await triggerRideSos(rideId);
    sosDataRef.current = {
      trackUrl: data?.trackUrl || '',
      contacts: Array.isArray(data?.contacts) ? data.contacts : [],
    };
    return sosDataRef.current;
  };

  const raiseServerAlert = () => {
    ensureSosData()
      .then(() => setAlerted(true))
      .catch((e) => console.warn('SOS server alert failed', e?.message));
  };

  const call112 = () => {
    raiseServerAlert();
    toast.success(tr('sos.teamNotified', 'Calling 112 — MyTownly safety team alerted'));
    window.location.href = buildSosTelUrl();
  };

  const callSupport = () => {
    if (!hasSupport) return;
    raiseServerAlert();
    window.location.href = supportTel;
  };

  const alertContact = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { contacts, trackUrl } = await ensureSosData();
      const msg = tr(
        'sos.alertMsg',
        `SOS from MyTownly Anna (${APP.city}). I need help. Live trip: ${trackUrl}`,
        { city: APP.city, track: trackUrl },
      );
      openWhatsappChat(msg, contacts[0]?.phone || '');
      setAlerted(true);
      toast.success(tr('sos.alertedDone', 'Alert sent'));
    } catch (e) {
      console.error(e);
      toast.error(tr('sos.alertFailed', 'Could not send alert'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal-sheet sos-sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>{tr('sos.title', 'Emergency')}</h3>
        <p className="text-muted">{tr('sos.subtitleDriver', 'Get help fast. Every action also alerts the MyTownly safety team.')}</p>

        <button type="button" className="sos-action sos-action-emergency" onClick={call112}>
          <span className="sos-action-ico">🚨</span>
          <span>
            <strong>{tr('sos.call112', 'Call 112')}</strong>
            <small>{tr('sos.call112Sub', 'India national emergency — police, fire, ambulance')}</small>
          </span>
        </button>
        <p className="sos-team-note">{tr('sos.teamNote', 'Calling 112 or support also alerts the MyTownly safety team with your live location.')}</p>

        <button type="button" className="sos-action" onClick={callSupport} disabled={!hasSupport}>
          <span className="sos-action-ico">📞</span>
          <span>
            <strong>{tr('sos.callSupport', 'Call MyTownly support')}</strong>
            <small>{tr('sos.callSupportSub', 'Talk to our team')}</small>
          </span>
        </button>

        <button type="button" className="sos-action sos-action-whatsapp" onClick={alertContact} disabled={busy}>
          <span className="sos-action-ico">👥</span>
          <span>
            <strong>{alerted ? tr('sos.alertedDone', 'Alert sent') : tr('sos.alertContact', 'Alert my emergency contact')}</strong>
            <small>{tr('sos.alertContactHint', 'Sends your live trip link on WhatsApp')}</small>
          </span>
        </button>

        <button type="button" className="btn btn-ghost" onClick={onClose} style={{ marginTop: 8 }}>
          {tr('sos.close', 'Close')}
        </button>
      </div>
    </div>
  );
}
